import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileSignature, CheckCircle2, XCircle, FileText, Download } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const baixarPdf = async (aid, doc, nome) => {
  try {
    const r = await api.get(`/condicoes-aceite/${aid}/documento.pdf`, { params: { doc }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = nome; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error("Não foi possível baixar o PDF"); }
};

const STATUS = {
  pendente: { l: "Aguardando seu aceite", c: "bg-aj-amber-soft text-aj-orange-hover" },
  aceito: { l: "Aceito", c: "bg-green-100 text-green-700" },
  recusado: { l: "Recusado", c: "bg-red-100 text-red-700" },
  substituido: { l: "Substituído", c: "bg-gray-100 text-gray-600" },
};

export default function MontadorContratos() {
  const [lista, setLista] = useState(null);
  const [det, setDet] = useState(null);
  const [recusa, setRecusa] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [agindo, setAgindo] = useState(false);
  const [docView, setDocView] = useState(null); // { aid, titulo, texto, doc }

  const abrirDoc = async (c, tipo) => {
    try {
      const r = await api.get(`/condicoes-aceite/${c.id}/documentos`);
      if (tipo === "aditivo") {
        setDocView({ aid: c.id, doc: "aditivo", titulo: "Termo Aditivo Comercial e Operacional", texto: r.data.aditivo });
      } else {
        setDocView({ aid: c.id, doc: "contrato", titulo: "Contrato de Prestação de Serviços (assinado)", texto: r.data.contrato_base });
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = () => api.get("/montador/contratos").then(r => setLista(r.data))
    .catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const aceitar = async (c) => {
    setAgindo(true);
    try {
      await api.post(`/montador/contratos/${c.id}/aceitar`);
      toast.success("Contrato aceito! Registro jurídico gravado.");
      setDet(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAgindo(false); }
  };

  const recusar = async () => {
    if (!motivo.trim()) { toast.error("Informe o motivo"); return; }
    setAgindo(true);
    try {
      await api.post(`/montador/contratos/${recusa.id}/recusar`, { motivo });
      toast.success("Contrato recusado");
      setRecusa(null); setMotivo(""); setDet(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAgindo(false); }
  };

  return (
    <div className="space-y-6 p-1" data-testid="montador-contratos-page">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Comercial</div>
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-aj-blue-deep mt-1">Contratos Comerciais</h1>
        <p className="text-sm text-aj-text-muted mt-1 max-w-2xl">
          Contratos enviados pelas empresas com seus valores e condições de pagamento. O aceite é digital e auditado.
        </p>
      </div>

      {lista?.length === 0 && (
        <div className="aj-card p-10 text-center text-aj-text-muted" data-testid="mc-vazio">
          Nenhum contrato recebido ainda.
        </div>
      )}

      <div className="space-y-3">
        {lista?.map(c => {
          if (c.origem_condicoes) {
            return (
              <div key={c.id} className="aj-card p-5 border-l-4 border-l-green-500" data-testid={`mc-aceite-${c.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <FileSignature size={16} className="text-aj-orange" />
                      <span className="font-display font-bold text-aj-blue-deep">{c.empresa_nome}</span>
                      <span className="font-mono text-[10px] text-aj-text-muted">v{c.versao}</span>
                    </div>
                    <div className="text-xs text-aj-text-muted mt-1">
                      Contrato assinado digitalmente em {c.aceito_em ? new Date(c.aceito_em).toLocaleString("pt-BR") : "—"}
                      {c.hash_documento && <span className="font-mono"> · hash {String(c.hash_documento).slice(0, 12)}…</span>}
                    </div>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-green-100 text-green-700">Aceito</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {c.contrato_proprio ? (
                    <a href={`${BASE}/api/condicoes/contrato/${c.contrato_proprio.id}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs underline text-[#142766] font-semibold"
                      data-testid={`mc-contrato-proprio-${c.id}`}>
                      <FileText size={13} /> Contrato da empresa: {c.contrato_proprio.nome}
                    </a>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => abrirDoc(c, "contrato")} data-testid={`mc-ver-contrato-${c.id}`}>
                        <FileText size={13} className="mr-1" /> Ver contrato
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => baixarPdf(c.id, "contrato", `contrato_${c.empresa_nome}.pdf`)}
                        data-testid={`mc-pdf-contrato-${c.id}`}>
                        <Download size={13} className="mr-1" /> Contrato (PDF)
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="outline" onClick={() => abrirDoc(c, "aditivo")} data-testid={`mc-ver-aditivo-${c.id}`}>
                    <FileText size={13} className="mr-1" /> Ver termo aditivo
                  </Button>
                  <Button size="sm" variant="outline"
                    onClick={() => baixarPdf(c.id, "aditivo", `termo_aditivo_${c.empresa_nome}.pdf`)}
                    data-testid={`mc-pdf-aditivo-${c.id}`}>
                    <Download size={13} className="mr-1" /> Termo aditivo (PDF)
                  </Button>
                </div>
              </div>
            );
          }
          const st = STATUS[c.status] || STATUS.pendente;
          return (
            <div key={c.id} className="aj-card p-5" data-testid={`mc-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <FileSignature size={16} className="text-aj-orange" />
                    <span className="font-display font-bold text-aj-blue-deep">{c.empresa_nome}</span>
                    <span className="font-mono text-[10px] text-aj-text-muted">v{c.versao}</span>
                  </div>
                  <div className="text-xs text-aj-text-muted mt-1 capitalize">
                    Pagamento {c.periodicidade} · {c.dia_pagamento} · {c.forma_pagamento} · {(c.itens || []).length} tipo(s) de serviço
                  </div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${st.c}`}>{st.l}</span>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setDet(c)} data-testid={`mc-ver-${c.id}`}>
                  Ler contrato
                </Button>
                {c.status === "pendente" && (
                  <>
                    <Button size="sm" className="bg-aj-success hover:bg-green-700 text-white"
                      onClick={() => aceitar(c)} disabled={agindo} data-testid={`mc-aceitar-${c.id}`}>
                      <CheckCircle2 size={14} className="mr-1" /> Aceitar
                    </Button>
                    <Button size="sm" variant="outline" className="text-aj-danger border-aj-danger"
                      onClick={() => setRecusa(c)} data-testid={`mc-recusar-${c.id}`}>
                      <XCircle size={14} className="mr-1" /> Recusar
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!det} onOpenChange={(o) => !o && setDet(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Contrato · {det?.empresa_nome}</DialogTitle></DialogHeader>
          {det && (
            <div className="space-y-3 text-sm">
              <div className="border-l-4 border-aj-blue p-4 bg-aj-surface whitespace-pre-wrap font-mono text-xs leading-relaxed"
                data-testid="mc-texto">{det.texto}</div>
              {det.aceito_em && (
                <div className="text-xs text-green-700">✓ Aceito em {new Date(det.aceito_em).toLocaleString("pt-BR")}</div>
              )}
              {det.status === "pendente" && (
                <DialogFooter>
                  <Button variant="outline" className="text-aj-danger border-aj-danger"
                    onClick={() => setRecusa(det)} data-testid="mc-dialog-recusar">Recusar</Button>
                  <Button className="bg-aj-success hover:bg-green-700 text-white" disabled={agindo}
                    onClick={() => aceitar(det)} data-testid="mc-dialog-aceitar">
                    {agindo ? "Registrando…" : "Aceitar contrato"}
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!docView} onOpenChange={o => !o && setDocView(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="mc-doc-dialog">
          <DialogHeader><DialogTitle className="text-base">{docView?.titulo}</DialogTitle></DialogHeader>
          <div className="overflow-y-auto flex-1 border border-[#E2E8F0] bg-[#FAFAFA] p-4">
            <pre className="whitespace-pre-wrap text-xs font-sans leading-relaxed" data-testid="mc-doc-texto">{docView?.texto}</pre>
          </div>
          <Button variant="outline" onClick={() => baixarPdf(docView.aid, docView.doc, `${docView.doc}.pdf`)}
            data-testid="mc-doc-baixar-pdf">
            <Download size={14} className="mr-1" /> Baixar PDF
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!recusa} onOpenChange={(o) => !o && setRecusa(null)}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Recusar contrato</DialogTitle></DialogHeader>
          <Textarea rows={3} placeholder="Explique o motivo da recusa *" value={motivo}
            onChange={(e) => setMotivo(e.target.value)} data-testid="mc-motivo" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecusa(null)}>Cancelar</Button>
            <Button className="bg-aj-danger text-white" onClick={recusar} disabled={agindo}
              data-testid="mc-confirmar-recusa">Confirmar recusa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
